import axios from 'axios';

import {
	getSession,
} from 'Vulture/Session.js';

import {
	broncoURL,
	commitHash,
} from 'Vulture/ENV.js';

import {
	getInitialSchedulingMode,
} from 'Crow/Form/Field/Custom/Vulture/utils/scheduling.js';

export const libraryBucketName = 'ts-condor-assets';

export const prepPendingSlideshowChanges = (pendingSlideshowChanges) => {
	const keys = [];
	const undoKeys = [];
	const vals = [];
	const undoVals = [];

	Object.entries(pendingSlideshowChanges).forEach(([ key, pendingSlideshowChange, ]) => {
		if (key === 'order') {
			return;
		}

		const oldSlide = pendingSlideshowChange.oldSlide;
		const newSlide = pendingSlideshowChange.newSlide;

		const oldComponentUUIDs = [ ...oldSlide.component_uuids, ];
		const newComponentUUIDs = [ ...newSlide.component_uuids, ];

		if (JSON.stringify(oldComponentUUIDs) !== JSON.stringify(newComponentUUIDs)) {
			const updatedSlideComponentUUIDs = updateSlideComponentUUIDs(newSlide, oldComponentUUIDs);
			keys.push(...updatedSlideComponentUUIDs.keys);
			undoKeys.push(...updatedSlideComponentUUIDs.undoKeys);
			vals.push(...updatedSlideComponentUUIDs.vals);
			undoVals.push(...updatedSlideComponentUUIDs.undoVals);
		}
	});

	return {
		keys,
		vals,
		undoKeys,
		undoVals,
	};
};

// The resolved upload path isn't known until the server processes the upload, so
// the front-end stages this token as a slideshow slide's pelican_json `file` and
// /api/slideshow/slide substitutes the real path. Must match the literal in
// bronco controller/slideshow.go.
export const UPLOADED_FILE_PATH_PLACEHOLDER = '__TS_UPLOADED_FILE_PATH__';

// Uploads a file to the per-account uploads folder.
//
// Without `data`: returns the server-resolved path; callers must use it rather
// than re-synthesizing one (the server may collision-rename).
//
// With `data` (the DD map app.saveData would POST to /w/edit): the upload and the
// slide-reference write happen atomically via /api/slideshow/slide. Stage
// UPLOADED_FILE_PATH_PLACEHOLDER as the slide's file; the server substitutes the
// resolved path. Returns the /w/edit response ({ keys, ... }).
export const addObject = async (file, fileName, accountUUID, data = null) => {
	const session = getSession();

	const formData = new FormData();
	formData.append('file', file);
	formData.append('file_name', fileName);

	const headers = {
		'X-Auth-Session':      session.session,
		'X-Spark-API-version': commitHash,
	};

	if (data) {
		formData.append('data', JSON.stringify(data));

		const url = `${broncoURL}/api/slideshow/slide?account_uuid=${accountUUID}`;
		const response = await axios.post(url, formData, {
			headers,
		});

		return response.data;
	}

	const url = `${broncoURL}/api/folder/upload/file?account_uuid=${accountUUID}`;
	const response = await axios.post(url, formData, {
		headers,
	});

	return response.data;
};

// Uploads a file and creates (or, with meta.slide_deck_slide_id, repoints) the
// slide_deck_slide in one transaction. Slide content (kind/name/resolution/
// file_size/file) is derived server-side from the upload; `meta` carries the
// slide_deck_slide fields. Returns { slide, slide_deck_slide }.
export const createSlideDeckSlideWithUpload = async (file, fileName, accountUUID, slideDeckID, meta = {}) => {
	const session = getSession();

	const url = `${broncoURL}/api/slide_deck/${slideDeckID}/slide?account_uuid=${accountUUID}`;

	const formData = new FormData();
	formData.append('file', file);
	formData.append('file_name', fileName);
	formData.append('meta', JSON.stringify(meta));

	const response = await axios.post(url, formData, {
		headers: {
			'X-Auth-Session':      session.session,
			'X-Spark-API-version': commitHash,
		},
	});

	return response.data;
};

// getPdfViewURL asks bronco for a short-lived presigned URL that serves the PDF
// inline (Content-Type application/pdf), so legacy PDFs stored as octet-stream
// render in-browser instead of downloading. Rejects if the file is missing or
// inaccessible, so callers can show a clean error instead of a broken viewer.
export const getPdfViewURL = async (fileUrl, accountUUID) => {
	const session = getSession();

	const url = `${broncoURL}/api/file/view-url?account_uuid=${accountUUID}&url=${encodeURIComponent(fileUrl)}`;

	const response = await axios.get(url, {
		headers: {
			'X-Auth-Session':      session.session,
			'X-Spark-API-version': commitHash,
		},
	});

	return response.data.url;
};

export const structureAddJSON = (formData, objKey) => {
	// Check for video thumbnails and remove --video.jpg from JSON file.
	if (~objKey.indexOf('--video.jpg')) {
		objKey = objKey.replace('--video.jpg', '');
	}

	const newObj = {
		file:            objKey,
		kind:            formData.kind || formData.fileType,
		description:     formData.description || '',
		duration:        formData.duration || '7',
		order:           formData.order || '1',
		tags:            formData.tags || [],
		resolution:      formData.resolution || '',
		is_from_library: formData.is_from_library || false,
		component_type:  'pelican_slide',
	};

	if (formData.scheduled !== undefined) {
		// New scheduling format
		newObj.scheduled = formData.scheduled || false;
		newObj.scheduling = formData.scheduling || {};
		newObj.schedulingMode = formData.schedulingMode ?? getInitialSchedulingMode(formData.scheduling);
	} else {
		// Old scheduling format
		newObj.scheduling = formData.scheduling || false;
		newObj.days = formData.days || [];
		newObj.end_date = formData.end_date || '';
		newObj.end_time = formData.end_time || '';
		newObj.start_date = formData.start_date || '';
		newObj.start_time = formData.start_time || '';
		newObj.use_dates = formData.use_dates || false;
		newObj.use_days = formData.use_days || false;
		newObj.use_times = formData.use_times || false;
	}

	return newObj;
};

export const structureEditJSON = (formData) => {
	// Check for video thumbnails and remove --video.jpg from JSON file.
	if (~formData.key.indexOf('--video.jpg')) {
		formData.key = formData.key.replace('--video.jpg', '');
	}

	const newObj = {
		file:            formData.file,
		kind:            formData.kind,
		description:     formData.description,
		duration:        formData.duration,
		order:           formData.order,
		tags:            formData.tags,
		resolution:      formData.resolution,
		component_type:  formData.component_type,
		is_from_library: formData.is_from_library,
	};

	// Migrated slides carry a `migration` marker ({ project_number, slideshow })
	// that the stamping step uses to (re)attach them to each system's Slideshow.
	// Keep it across edits so a re-stamp still finds the slide. (DEV-12122)
	if (formData.migration) {
		newObj.migration = formData.migration;
	}

	if (formData.scheduled !== undefined) {
		// New scheduling format
		newObj.scheduled = formData.scheduled;
		newObj.scheduling = formData.scheduling;
		newObj.schedulingMode = formData.schedulingMode;
	} else {
		// Old scheduling format
		let normalizedDays = formData.days;
		let normalizedUseDays = formData.use_days;

		if (formData && formData.days && formData.days.length === 7) {
			normalizedDays = [];
			normalizedUseDays = false;
		}
		newObj.scheduling = formData.scheduling;
		newObj.days = normalizedDays;
		newObj.end_date = formData.end_date;
		newObj.end_time = formData.end_time;
		newObj.start_date = formData.start_date;
		newObj.start_time = formData.start_time;
		newObj.use_dates = formData.use_dates;
		newObj.use_days = normalizedUseDays;
		newObj.use_times = formData.use_times;
	}

	return newObj;
};

export const addSlideData = (newSlide, objKey, pelicanJsonUUID) => {
	const keys = [];
	const undoKeys = [];
	const vals = [];
	const undoVals = [];
	const slide_uuid = newSlide.key;

	const slideData = structureAddJSON(newSlide, objKey);

	keys.push(`component.${slide_uuid}.setting.${pelicanJsonUUID}.json`);
	undoKeys.push(`component.${slide_uuid}.setting.${pelicanJsonUUID}`);
	vals.push(JSON.stringify(slideData));
	undoVals.push(false);

	return {
		keys,
		vals,
		undoKeys,
		undoVals,
	};
};

export const updateSlideData = (updatedSlide, pelicanJsonUUID) => {
	const keys = [];
	const undoKeys = [];
	const vals = [];
	const undoVals = [];
	const slide_uuid = updatedSlide.key;

	const slideData = structureEditJSON(updatedSlide);

	keys.push(`component.${slide_uuid}.setting.${pelicanJsonUUID}.json`);
	undoKeys.push(`component.${slide_uuid}.setting.${pelicanJsonUUID}`);
	vals.push(JSON.stringify(slideData));
	undoVals.push(false);

	return {
		keys,
		vals,
		undoKeys,
		undoVals,
	};
};

export const addSlideComponentUUIDs = (updatedSlide) => {
	const keys = [];
	const undoKeys = [];
	const vals = [];
	const undoVals = [];
	const slide_uuid = updatedSlide.key;

	updatedSlide.component_uuids.forEach((UUID) => {
		keys.push(`component.${UUID}.component.${slide_uuid}`);
		undoKeys.push(`component.${UUID}.component.${slide_uuid}`);
		vals.push(true);
		undoVals.push(false);
	});

	return {
		keys,
		vals,
		undoKeys,
		undoVals,
	};
};

export const updateSlideComponentUUIDs = (updatedSlide, oldComponentUUIDs) => {
	const keys = [];
	const undoKeys = [];
	const vals = [];
	const undoVals = [];
	const slide_uuid = updatedSlide.key;

	updatedSlide.component_uuids.forEach((UUID) => {
		if (!oldComponentUUIDs.includes(UUID)) {
			keys.push(`component.${UUID}.component.${slide_uuid}`);
			undoKeys.push(`component.${UUID}.component.${slide_uuid}`);
			vals.push(true);
			undoVals.push(false);
		}
	});

	oldComponentUUIDs.forEach((UUID) => {
		if (!updatedSlide.component_uuids.includes(UUID)) {
			keys.push(`component.${UUID}.component.${slide_uuid}`);
			undoKeys.push(`component.${UUID}.component.${slide_uuid}`);
			vals.push(false);
			undoVals.push(true);
		}
	});

	return {
		keys,
		vals,
		undoKeys,
		undoVals,
	};
};

export const fetchContentSidebar = async (accountUUID, systemGroupUUID) => {
	const session = getSession();

	let url = `${broncoURL}/account/content`;
	let body = {
		user:         session.user,
		account:      accountUUID,
		system_group: systemGroupUUID,
	};

	const headers = {
		headers: {
			'X-Auth-Session':      session.session,
			'X-Spark-API-version': commitHash,
		},
	};

	try {
		let res = await axios.post(url, body, headers);
		if (res.status !== 200) {
			debugger;
		}

		return res.data;
	} catch (err) {
		console.error(err);
		return {
			'error':   true,
			'message': err.message,
		};
	}
};

export const fetchPelicanData = async (accountUUID, pelicanUUIDS) => {
	const session = getSession();

	let url = `${broncoURL}/pelican`;
	let body = {
		account_uuid:       accountUUID,
		pelican_uuids:      pelicanUUIDS,
		include_unassigned: true,
	};

	const headers = {
		headers: {
			'X-Auth-Session': session.session,
		},
	};

	try {
		let res = await axios.post(url, body, headers);
		if (res.status !== 200) {
			debugger;
		}

		return res.data;
	} catch (err) {
		console.error(err);
		throw new Error({
			error:   true,
			message: err.message,
		});
	}
};

export const fetchListingDataByListingConfig = async (accountUUID, systemGroupUUID, listingConfigUUID) => {
	const session = getSession();

	let url = `${broncoURL}/listing_config/listing_collections`;
	let body = {
		account_uuid:        accountUUID,
		system_group_uuid:   systemGroupUUID,
		listing_config_uuid: listingConfigUUID,
	};

	const headers = {
		headers: {
			'X-Auth-Session': session.session,
		},
	};

	try {
		let res = await axios.post(url, body, headers);
		if (res.status !== 200) {
			debugger;
		}

		return res.data;
	} catch (err) {
		console.error(err);
	}
};

export const deleteListings = async (listingUUIDs) => {
	const session = getSession();

	const headers = {
		headers: {
			'X-Auth-Session': session.session,
		},
	};

	let url = `${broncoURL}/delete/listings`;
	let body = {
		listing_uuids: listingUUIDs,
	};

	try {
		let res = await axios.post(url, body, headers);
		if (res.status !== 200) {
			debugger;
		}

		return res.data;
	} catch (err) {
		console.error(err);
	}
};

// deleteSlideshowSlides removes 1..N slideshow slides. The backend decides per slide
// whether to unassign it from the current system group (when shared with other groups)
// or remove it outright; pass systemGroupUUID = '' for an account-level delete.
export const deleteSlideshowSlides = async (accountUUID, systemGroupUUID, slideUUIDs) => {
	const session = getSession();

	const headers = {
		headers: {
			'X-Auth-Session': session.session,
		},
	};

	let url = `${broncoURL}/delete/slideshow/slides`;
	let body = {
		account_uuid:      accountUUID,
		system_group_uuid: systemGroupUUID || '',
		slide_uuids:       slideUUIDs,
	};

	try {
		let res = await axios.post(url, body, headers);
		if (res.status !== 200) {
			throw new Error(`deleteSlideshowSlides unexpected status: ${res.status}`);
		}

		return res.data;
	} catch (err) {
		// Log for parity with the other helpers, then rethrow so callers'
		// .catch() can keep the slides in the UI when the delete failed.
		console.error(err);
		throw err;
	}
};

export const generateConcatenatedColumns = async (listingConfigUUID, listingUUID) => {
	const session = getSession();

	const headers = {
		headers: {
			'X-Auth-Session': session.session,
		},
	};

	const url = `${broncoURL}/listing_config/generate_concatenated_columns`;
	const body = {
		listing_config_uuid: listingConfigUUID,
		listing_uuid:        listingUUID,
	};

	try {
		const res = await axios.post(url, body, headers);
		if (res.status !== 200) {
			debugger;
		}

		return res.data;
	} catch (err) {
		console.error(err);
	}
};